<template>
  <transition enter-active-class="animated slideInFaster" leave-active-class="animated slideOutFaster">
    <aside v-if="clientShow && client" class="client-view">
      <fee-modal :displayed="feeModalDisplayed" @close="closeFeeModal()" />
      <confirm :title="$t('Remove this client?')" :displayed="deleteClientDisplayed">
        <p slot="body">
          <span v-t>If</span>
          &nbsp; {{ client.firstName }} {{ client.lastName }} &nbsp;
          <span v-t>clicks on their invite link, it will no longer work.</span>
        </p>
        <div slot="footer" class="remove-client-buttons">
          <button v-t type="button" class="outline modal-buttons" @click="removeClient">Yes, Remove</button>
          <button v-t type="button" class="subtle modal-buttons" @click="closeRemoveClientModal">No, Keep</button>
        </div>
      </confirm>
      <confirm :title="$t('Remove this client?')" :displayed="unlinkClientDisplayed">
        <p slot="body">
          <span v-t>You can always re-invite them in the future.</span>
        </p>
        <div slot="footer" class="remove-client-buttons">
          <button v-t type="button" class="outline modal-buttons" @click="unlinkClient">Yes, Remove</button>
          <button v-t type="button" class="subtle modal-buttons" @click="closeUnlinkClientModal">No, Keep</button>
        </div>
      </confirm>
      <div class="client-profile-container">
        <header class="client-profile-nav">
          <div>
            <router-link :to="'/app/clients/'" class="back-close-button">
              <svgicon class="close-icon" name="close" :alt="$t('Close')" />
              <svgicon class="back-icon" name="arrow-left" :alt="$t('Back')" />
              <h4 class="name-title">{{ client.firstName }} {{ client.lastName }}</h4>
            </router-link>
          </div>
        </header>
        <div v-if="false" class="dashboard-action row">
          <a><svgicon name="pie" :alt="$t('dashboard')" /><span v-t>See Dashboard</span></a>
        </div>
        <section>
          <div class="client-summary">
            <h5 v-t class="row">Client Summary</h5>
            <ul class="no-margin">
              <li v-show="client.joinedAt" class="row">
                <span>
                  <span v-t class="label join">Date joined:</span>
                  {{ client.joinedAt | formatDate }}
                </span>
              </li>
              <li v-show="client.declinedAt" class="row">
                <span>
                  <span v-t class="label test">Date declined:</span>
                  {{ client.declinedAt | formatDate }}
                </span>
              </li>
              <li v-show="client.invitedAt && !client.declinedAt" class="row">
                <span>
                  <span v-t class="label invite">Date invited:</span>
                  {{ client.invitedAt | formatDate }}
                </span>
              </li>
              <li class="row">
                <span>
                  <span v-t class="label">Email:</span>&nbsp;
                  <a class="email black" :href="`mailto:${client.email}`">{{ client.email }}</a>
                </span>
              </li>
              <li class="row">
                <span>
                  <span v-t class="label">Phone:</span>&nbsp;
                  <a class="black" :href="`tel:${client.phone}`">{{ client.phone }}</a>
                </span>
              </li>
              <li class="row">
                <span>
                  <span v-t class="label">Planning Fee:</span>
                  <span class="tab-numbers">&nbsp;{{ client.fee | formatPercentage }}</span>
                </span>
                <a v-t href="#" @click="showFeeModal()">Edit</a>
              </li>
            </ul>
          </div>
          <div v-if="client.joinedAt" class="account-summary">
            <h5 v-t class="row">Account Summary</h5>
            <div class="row total-row">
              <span v-t class="label">Total</span>
              <span class="tab-numbers">{{ client.assets | formatCurrency }}</span>
            </div>
            <expandable-section v-for="account in client.accounts" :key="account.id" class="expandable-section">
              <div slot="title" class="accordion-title">
                <span class="label">{{ account.accountTypeName }}</span>
                <span class="tab-numbers">{{ account.balance | formatCurrency }}</span>
              </div>
              <div slot="content">
                <div class="account-data">
                  <div>
                    <span v-t class="label">Portfolio</span>
                    <span class="tab-numbers">{{ account.portfolioModelName | valueOrDash }}</span>
                  </div>
                  <div>
                    <span v-t class="label">Inception date</span>
                    <span class="tab-numbers">{{ account.inceptedAt | formatDate }}</span>
                  </div>
                </div>
                <div class="account-data">
                  <div>
                    <span v-t class="label">Rate of return</span>
                    <span class="tab-numbers">{{ account.ror | formatPercentage }}</span>
                  </div>
                  <div>
                    <span v-t class="label">Net contributions</span>
                    <span class="tab-numbers">{{ account.contributions | formatCurrency }}</span>
                  </div>
                </div>
                <div class="account-data">
                  <div>
                    <span v-t class="label">Account number</span>
                    <span class="tab-numbers">{{ account.custodianAccountId ? `#${account.custodianAccountId}` : null | valueOrDash }}</span>
                  </div>
                </div>
              </div>
            </expandable-section>
          </div>
          <div v-if="client.shareInfo === false">
            <small v-t class="share-info-explanation subtle">
              This user has opted out of sharing investment details and activities with you.
            </small>
          </div>
          <div class="link-container">
            <router-link
              v-if="client.accounts && client.accounts.length > 0"
              :to="client.accounts.length > 1 ? `/app/clients/${client.id}/overview` : `/app/clients/${client.id}/${client.accounts[0].id}`"
              target="_blank" rel="noopener noreferrer"
              class="button flat icon before"
            >
              <svgicon class="open-icon" name="open" :alt="$t('Open')" />
              <span v-t>Client Dashboard</span>
            </router-link>
          </div>
        </section>
        <button v-if="client.invitedAt" v-t type="button" class="subtle remove-client-button" @click="showRemoveClientModal()">Remove</button>
        <button
          v-if="client.joinedAt && !client.isFunded"
          v-t
          type="button"
          class="subtle remove-client-button"
          @click="showUnlinkClientModal()"
        >
          Remove
        </button>
      </div>
    </aside>
  </transition>
</template>

<script>
import { mapGetterMethods, mapActions } from '@/lib/vue';
import filters from '@/lib/utils/filters';
import getBrandConfig from '@/lib/utils/get_brand_config';
import { feeModal } from '@/components/advisor';
import { confirm, expandableSection } from '@/components/layout';
import '@/lib/icons/arrow-left';
import '@/lib/icons/pie';
import '@/lib/icons/open';
import errorStore from '@/store/error';

const _ = { errorStore };

const brandConfig = getBrandConfig();

export default {
  _,
  filters,
  components: {
    feeModal,
    confirm,
    expandableSection,
  },
  head() {
    if (!this.client) return { title: `Clients | ${brandConfig.fullName}` };
    return {
      title: `${this.client.firstName} ${this.client.lastName} | ${brandConfig.fullName}`,
    };
  },

  data() {
    return {
      feeModalDisplayed: false,
      deleteClientDisplayed: false,
      unlinkClientDisplayed: false,
      clientShow: false,
    };
  },
  computed: {
    client() {
      return this.getClient(this.$route.params.id);
    },
  },
  mounted() {
    this.clientShow = true;
  },
  methods: {
    ...mapGetterMethods(['getClient']),
    ...mapActions(['deleteClient', 'unlinkUnfundedClient']),
    showFeeModal() {
      this.feeModalDisplayed = true;
    },
    closeFeeModal() {
      this.feeModalDisplayed = false;
    },
    showRemoveClientModal() {
      this.deleteClientDisplayed = true;
    },
    closeRemoveClientModal() {
      this.deleteClientDisplayed = false;
    },
    showUnlinkClientModal() {
      this.unlinkClientDisplayed = true;
    },
    closeUnlinkClientModal() {
      this.unlinkClientDisplayed = false;
    },
    async removeClient() {
      try {
        await this.deleteClient(this.client.id);
        this.deleteClientDisplayed = false;
      } catch (exception) {
        _.errorStore.addError();
      }
    },
    async unlinkClient() {
      try {
        await this.unlinkUnfundedClient(this.client.id);
        this.unlinkClientDisplayed = false;
      } catch (exception) {
        _.errorStore.addError();
      }
    },
  },
};
</script>

<style lang="scss" scoped>


  a.black {
    color: $black;
  }

  .slideInFaster {
    animation-name: slideInRight;
    animation-duration: 0.3s;
  }

  .slideOutFaster {
    animation-name: slideOutRight;
    animation-duration: 0.3s;
  }

  .client-profile-container {
    overflow: auto;
    max-height: 100%;
    padding-bottom: 2rem;

    @media #{$screen-width-medium} {
      padding-bottom: 5rem;
    }
  }

  .client-view {
    position: absolute;
    overflow: visible;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background-color: $pearl-50;
    z-index: 10;

    @media #{$screen-width-medium} {
      width: 26rem;
      // top: 3rem;
      box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.2);
    }
  }

  .client-profile-nav {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    padding: 0.75rem;
    background-color: $white;

    > div {
      display: flex;
      align-items: center;

      .name-title {
        position: relative;
        margin: 0;
        left: 0.25rem;
      }
    }

    a {
      text-decoration: none;
    }

    .back-close-button {
      display: flex;
      align-items: center;
      margin-right: 0.5rem;
      color: $black;

      .close-icon, .back-icon {
        stroke: $black;
      }

      .close-icon {
        display: none;
      }

      @media #{$screen-width-medium} {
        .back-icon {
          display: none;
        }

        .close-icon {
          display: inline-block;
        }
      }
    }

    @media #{$screen-width-medium} {
      padding: 1rem;
    }
  }

  .dashboard-action {
    a {
      display: flex;
      align-items: center;
      text-decoration: none;
    }

    svg {
      margin-right: 0.25rem;
    }
  }

  .client-summary,
  .account-summary {
    > :nth-child(n + 2) {
      background-color: $white;
    }
  }

  .client-profile-nav,
  .row {
    border-bottom: 1px solid $pearl-300;
  }

  .label {
    color: $pearl-700;
  }

  .row {
    margin: 0;
    padding: 0.75rem 0.5rem;
    display: flex;
    justify-content: space-between;
  }

  li.row {
    > .label {
      margin-right: 0.25rem;
    }
  }

  .remove-client-buttons {
    padding: 0.5rem;
    display: flex;
    width: 100%;
    justify-content: space-around;
  }

  .modal-buttons {
    width: 8rem;
  }

  .share-info-explanation {
    display: block;
    padding: 1rem;
    text-align: center;
  }

  .link-container {
    text-align: center;

    .dashboard-link {
      display: inline-block;
      margin-top: 0.5rem;
      background-color: $pearl-50;
      text-decoration: none;

      .open-icon {
        bottom: 1px;
      }
    }
  }

  .remove-client-button {
    margin: 1rem 0.5rem;
  }

  .total-row {
    padding-right: 2rem;
  }

  .accordion-title {
    width: 100%;
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
  }

  .expandable-section {
    border-bottom: 1px solid $pearl-300;
  }

  .account-data {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    margin: 0.25rem 0.5rem 0.75rem;

    > div {
      width: 50%;

      > span {
        display: block;
      }
    }
  }

</style>
